import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CoreService } from '../../../services/core.service';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { StepperGridComponent } from "../stepper-grid/stepper-grid.component";
import { Router } from '@angular/router';
import { LatestPostsSidebarComponent } from "../latest-posts-sidebar/latest-posts-sidebar.component";

@Component({
  selector: 'app-upload-images-step',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatIconModule, MatButtonModule, StepperGridComponent, LatestPostsSidebarComponent],
  templateUrl: './social-ai-upload-images.component.html',
  styleUrls: ['./social-ai-upload-images.component.scss']
})
export class SocialAiUploadImagesComponent {
  @Input() companyID = 0;
  @Input() formData: any = {};
  @Output() submitted = new EventEmitter<any>();

  imagesForm: FormGroup;
  uploadedFiles: { file: File | null; preview: string; alt: string }[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly coreService: CoreService,
    private readonly router: Router
  ) {
    this.imagesForm = this.fb.group({
      company_id: [null, Validators.required],
      image_array: [null, Validators.required]
    });
  }

  ngOnInit(): void {
    this.companyID = this.companyID || this.coreService.user?.companyID || 0;

    this.imagesForm.patchValue({
      company_id: this.companyID,
      ...this.formData
    });
  }

  onMultipleFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    Array.from(input.files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.uploadedFiles.push({ file, preview: e.target.result, alt: '' });
        this.updateFormArray();
      };
      reader.readAsDataURL(file);
    });

    input.value = '';
  }

  addFiles(files: FileList): void {
    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.uploadedFiles.push({ file, preview: e.target.result, alt: '' });
        this.updateFormArray();
      };
      reader.readAsDataURL(file);
    });
  }

  removeFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
    this.updateFormArray();
  }

  clearAll(): void {
    this.uploadedFiles = [];
    this.updateFormArray();
  }

  dragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  dragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onFileDropped(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer?.files) {
      this.addFiles(event.dataTransfer.files);
    }
  }

  private updateFormArray(): void {
    this.imagesForm.patchValue({
      image_array: this.uploadedFiles.map((f) => f.file)
    });
  }

  submitImages(): void {

    if (!this.companyID) {
      this.coreService.showSnackbar('No company found.');
      return;
    }

    if (!this.uploadedFiles.length) {
      this.coreService.showSnackbar('Please select at least one image.');
      return;
    }

    const formData = new FormData();
    formData.append('company_id', this.companyID.toString());

    this.uploadedFiles.forEach((fileObj) => {
      if (fileObj.file) {
        formData.append('image_array[]', fileObj.file);
      }
    });

    this.coreService.postData('bulks/uploadImages', formData).subscribe({
      next: (res: any) => {
        if (res.success) {
          // this.coreService.showSnackbar('Images uploaded successfully!');
          this.submitted.emit(res);
          this.router.navigateByUrl('/upload-bulk/social-ai-download-file');
        } else {
          this.coreService.showSnackbar('Upload failed. Please try again.');
        }
      },
      error: () => {
        this.coreService.showSnackbar('Image upload failed. Please try again.');
      }
    });
  }
}
